import React from "react";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useApp } from "../AppContext";

const CartDrawer = () => {
    const { t, lang, cart, cartOpen, setCartOpen, updateQty, removeItem, cartTotal, fmtMoney } =
        useApp();

    const close = () => setCartOpen(false);
    const goCheckout = () => {
        close();
        setTimeout(() => {
            document.getElementById("order")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={close}
                className={`fixed inset-0 bg-earth-900/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
                    cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
            />
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-earth-50 z-50 shadow-2xl transform transition-transform duration-500 flex flex-col ${
                    cartOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between px-7 py-6 border-b border-earth-200">
                    <div className="flex items-center gap-3">
                        <ShoppingBag className="w-5 h-5 text-terracotta-600" />
                        <h3 className="font-serif text-2xl font-bold text-earth-900">
                            {t.cart.title}
                        </h3>
                    </div>
                    <button
                        data-testid="cart-close-btn"
                        onClick={close}
                        className="p-2 rounded-full hover:bg-earth-100"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-7 py-6">
                    {cart.length === 0 ? (
                        <div
                            className="text-center text-earth-600 py-16"
                            data-testid="cart-empty"
                        >
                            <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-earth-300" />
                            <p>{t.cart.empty}</p>
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {cart.map((c) => (
                                <li
                                    key={`${c.item_id}-${c.pre_order ? "po" : "reg"}`}
                                    data-testid={`cart-item-${c.slug}${c.pre_order ? "-preorder" : ""}`}
                                    className="flex gap-4 bg-white border border-earth-200 rounded-2xl p-3"
                                >
                                    <img
                                        src={c.image_url}
                                        alt=""
                                        className="w-20 h-20 rounded-xl object-cover shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-serif font-bold text-earth-900 truncate">
                                            {lang === "es" ? c.name_es : c.name_en}
                                        </div>
                                        {c.pre_order && (
                                            <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase tracking-wider">
                                                ⏳ {lang === "es" ? "Pre-orden 72h" : "Pre-order 72h"}
                                            </div>
                                        )}
                                        <div className="text-terracotta-600 font-bold text-sm mt-0.5">
                                            {fmtMoney(c.price)}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                            <button
                                                onClick={() => updateQty(c.item_id, -1, c.pre_order)}
                                                data-testid={`cart-dec-${c.slug}-btn`}
                                                className="w-7 h-7 rounded-full bg-earth-100 hover:bg-earth-200 flex items-center justify-center"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span className="text-sm font-bold w-6 text-center">
                                                {c.quantity}
                                            </span>
                                            <button
                                                onClick={() => updateQty(c.item_id, 1, c.pre_order)}
                                                data-testid={`cart-inc-${c.slug}-btn`}
                                                className="w-7 h-7 rounded-full bg-earth-100 hover:bg-earth-200 flex items-center justify-center"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={() => removeItem(c.item_id, c.pre_order)}
                                                data-testid={`cart-remove-${c.slug}-btn`}
                                                className="ml-auto p-1 text-earth-500 hover:text-terracotta-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border-t border-earth-200 px-7 py-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm uppercase tracking-wider font-semibold text-earth-600">
                            {t.cart.total}
                        </span>
                        <span
                            data-testid="cart-total"
                            className="font-serif font-black text-3xl text-earth-900"
                        >
                            {fmtMoney(cartTotal)}
                        </span>
                    </div>
                    <button
                        data-testid="cart-checkout-btn"
                        disabled={cart.length === 0}
                        onClick={goCheckout}
                        className="btn-primary w-full"
                    >
                        {t.cart.checkout}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default CartDrawer;
